import axios from 'axios'
import store from '@/store'

const API_BASE_URL = process.env.VUE_APP_API_HOST || 'http://localhost:8020'

// ── Refresh token state ────────────────────────────────────────
// Shared across all axios instances so concurrent 401s are queued
let isRefreshing = false
let failedQueue = []

/**
 * Drain the queue after a refresh attempt.
 * @param {Error|null} error  – pass error to reject all queued, null to resolve.
 * @param {string|null} token – new access token on success.
 */
function processQueue(error, token = null) {
  failedQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token)
  )
  failedQueue = []
}

// ── Plain axios for the refresh call (no interceptor loop) ─────
const refreshApi = axios.create({ baseURL: API_BASE_URL, timeout: 10000 })

export default function createAxios(baseURL, options = {}) {
  const instance = axios.create({
    baseURL,
    timeout: 10000,
    headers: { 'Content-Type': 'application/json' },
  })

  // ─── REQUEST INTERCEPTOR ────────────────────────────────────
  instance.interceptors.request.use(
    (config) => {
      // Let browser set boundary when sending FormData
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type']
      }

      if (options.useAuth) {
        const token = store.state.token
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // ─── RESPONSE INTERCEPTOR ───────────────────────────────────
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!error.response) {
        console.error('[Axios] Network error:', error.message)
        return Promise.reject(error)
      }

      const status   = error.response.status
      const original = error.config

      // ── 401 → try refresh token ──────────────────────────────
      if (status === 401 && options.useAuth && !original._retry) {
        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((newToken) => {
              original.headers.Authorization = `Bearer ${newToken}`
              return instance(original)
            })
            .catch((err) => Promise.reject(err))
        }

        original._retry = true
        isRefreshing     = true

        const refreshToken =
          localStorage.getItem('refresh_token') ||
          sessionStorage.getItem('refresh_token')

        if (!refreshToken) {
          isRefreshing = false
          processQueue(error)
          store.dispatch('logout')
          return Promise.reject(error)
        }

        try {
          const { data } = await refreshApi.post('/auth/token/refresh', {
            refreshToken: refreshToken,
          })

          const newToken        = data.data?.jwttoken || data.access
          const newRefreshToken = data.data?.refreshToken || data.refresh

          // Persist new tokens using the same storage that was previously used
          const storage = localStorage.getItem('token')
            ? localStorage
            : sessionStorage

          storage.setItem('token', newToken)
          if (newRefreshToken) storage.setItem('refresh_token', newRefreshToken)

          // Update Vuex store
          store.commit('SET_TOKEN', { token: newToken })

          // Update the header for the original retry
          original.headers.Authorization = `Bearer ${newToken}`

          processQueue(null, newToken)
          return instance(original)
        } catch (refreshError) {
          processQueue(refreshError)
          store.dispatch('logout')
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      // ── Other errors ─────────────────────────────────────────
      if (options.globalError !== false) {
        switch (status) {
          case 403:
            console.warn('[Axios] 403 Forbidden – access denied')
            break
          case 500:
            console.error('[Axios] 500 Internal Server Error')
            break
        }
      }

      return Promise.reject(error)
    }
  )

  return instance
}
